import { Box, Globe2, Loader2 } from 'lucide-react';
import { CatalogueSelect } from './CatalogueSelect';
import { useMaintenanceCatalogue } from './useMaintenanceCatalogue';
import { useCatalogueView } from './view';
import { useUI } from '@/state/store';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { useLoader } from '@/features/loader/store';

/** One header control for both modes, backed by the same catalogue and selection. */
export function NetworkPill({ maintenance }: { maintenance: boolean }) {
  const navigate = useNavigate();
  const examplesView = useCatalogueView((s) => s.examples);
  const selectedNetwork = useLoader(s => s.network);
  const examples = maintenance && (examplesView || selectedNetwork === 'full_catalogue');
  const { network, data, loading, error, retry } = useMaintenanceCatalogue(examples);
  const counts =
    network === 'rfims'
      ? '4 monitoring sites'
      : data && network === 'full_catalogue'
        ? `${data.missions.length.toLocaleString()} tracked objects`
        : data && !examples
        ? `${data.missions.length} missions, ${data.antennas.length} antennas`
        : null;
  return (
    <div
      data-testid="network-pill"
      className="flex min-w-0 max-w-full items-center gap-1 rounded-full border border-hairline bg-canvas/60 px-2 py-1.5"
    >
      <Globe2 aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
      <CatalogueSelect maintenance={maintenance} />
      {loading && (
        <Loader2 aria-label="Loading the network" className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-3" />
      )}
      {error && (
        <button
          type="button"
          title={error}
          onClick={retry}
          className="truncate text-[10px] text-warning underline"
        >
          Feed unavailable · retry
        </button>
      )}
      {counts && (
        <span
          title={counts}
          className="hidden truncate text-[10px] uppercase tracking-wider text-ink-3 min-[900px]:inline"
        >
          {counts}
        </span>
      )}
      {maintenance && (
        <button
          type="button"
          aria-pressed={examples}
          onClick={() => {
            useCatalogueView.getState().setExamples(true);
            useUI.getState().select({ assetId: null, subsystemId: null });
            navigate('/maintenance');
          }}
          className={cn(
            'ml-1 inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-accent',
            examples ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-overlay hover:text-ink',
          )}
        >
          <Box aria-hidden className="h-3.5 w-3.5" />
          Example models
        </button>
      )}
    </div>
  );
}
